import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, object=user
  const [companies, setCompanies] = useState([]);
  const [stores, setStores] = useState([]);
  const [permissions, setPermissions] = useState({}); // { "customers.delete": true, ... }
  const [activeCompanyId, setActiveCompanyIdState] = useState(
    localStorage.getItem("vetrich_company_id") || null
  );
  const [activeStoreId, setActiveStoreIdState] = useState(
    localStorage.getItem("vetrich_store_id") || "all"
  );

  const loadPermissions = useCallback(async () => {
    try {
      const { data } = await api.get("/permissions/mine", { silent: true });
      setPermissions(data.permissions || {});
    } catch { setPermissions({}); }
  }, []);

  const can = useCallback((module, action) => {
    // Admin → always allow.
    if (!user || user.role === "admin") return true;
    // HARD RULE: delete is an admin-only action across the whole app.
    if (action === "delete") return false;
    // Wait for permissions to hydrate before evaluating (empty map = loading).
    if (!permissions || Object.keys(permissions).length === 0) return true;
    return !!permissions[`${module}.${action}`];
  }, [user, permissions]);

  const setActiveCompanyId = useCallback((cid) => {
    if (cid) {
      localStorage.setItem("vetrich_company_id", cid);
    } else {
      localStorage.removeItem("vetrich_company_id");
    }
    setActiveCompanyIdState(cid);
  }, []);

  const setActiveStoreId = useCallback((sid) => {
    // Non-admin users are always locked to their assigned store — no-op.
    const val = sid || "all";
    localStorage.setItem("vetrich_store_id", val);
    setActiveStoreIdState(val);
  }, []);

  const loadStores = useCallback(async (currentUser) => {
    try {
      const { data } = await api.get("/stores", { silent: true });
      setStores(data);
      // Non-admins are pinned to their own store — mirror the backend rule
      // so the api.js interceptor sends the correct X-Store-Id header.
      // Managers WITH an assigned store are locked (peer of employees).
      const role = currentUser?.role;
      const locked =
        role === "employee" ||
        (role === "manager" && !!currentUser?.store_id);
      if (locked) {
        const pinned = currentUser.store_id || "all";
        localStorage.setItem("vetrich_store_id", pinned);
        setActiveStoreIdState(pinned);
      }
    } catch { setStores([]); }
  }, []);

  const loadCompanies = useCallback(async (currentUser) => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
      const storedId = localStorage.getItem("vetrich_company_id");
      const storedValid = storedId && data.find((c) => c.id === storedId);
      if (data.length && !storedValid) {
        // Either no stored id, or the stored one no longer exists — pick a sensible default
        const preferred = currentUser?.company_id && data.find((c) => c.id === currentUser.company_id)
          ? currentUser.company_id
          : data[0].id;
        setActiveCompanyId(preferred);
      }
    } catch (e) {
      // ignore
    }
  }, [setActiveCompanyId]);

  const checkSession = useCallback(async () => {
    try {
      // silent: don't show auto-toast for the initial "not signed in" 401
      const { data } = await api.get("/auth/me", { silent: true });
      setUser(data);
      await loadCompanies(data);
      await loadStores(data);
      await loadPermissions();
    } catch {
      setUser(false);
    }
  }, [loadCompanies, loadStores, loadPermissions]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    await loadCompanies(data);
    await loadStores(data);
    await loadPermissions();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setCompanies([]);
    setStores([]);
    setPermissions({});
    setActiveCompanyId(null);
    localStorage.removeItem("vetrich_store_id");
    setActiveStoreIdState("all");
  };

  // Reload permissions + stores whenever the active company changes (admin switches).
  useEffect(() => {
    if (user && user !== false) {
      loadPermissions();
      loadStores(user);
    }
  }, [activeCompanyId, user, loadPermissions, loadStores]);

  const value = {
    user,
    companies,
    stores,
    activeCompanyId,
    activeCompany: companies.find((c) => c.id === activeCompanyId) || null,
    activeStoreId,
    // Non-admins can't back-date entries — frontend hints via `min` on
    // <input type="date">; backend enforces `enforce_date_floor` too.
    dateFloor: user && user.role === "admin"
      ? ""
      : new Date().toISOString().slice(0, 10),
    activeStore: stores.find((s) => s.id === activeStoreId) || null,
    isGstCompany: !!(companies.find((c) => c.id === activeCompanyId)?.gstin || "").trim(),
    setActiveCompanyId,
    setActiveStoreId,
    reloadCompanies: () => loadCompanies(user),
    reloadStores: () => loadStores(user),
    permissions,
    can,
    reloadPermissions: loadPermissions,
    login,
    logout,
    formatApiError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
